/**
 * Verification Progress Component
 * Task 2.4: Implement Ghana Card Verification Frontend
 * 
 * User Story: "As a claimant, I must upload my Ghana Card and see real-time verification progress 
 * before submitting a claim, with clear feedback if verification fails."
 * 
 * This component shows the progress of Ghana Card verification with step indicators.
 */

import React from 'react';
import { PENDING_VERIFICATION, VERIFIED, VERIFICATION_FAILED } from '../../../constants/verificationStatus';

const VerificationProgress = ({ 
  status, 
  currentStep = 1,
  totalSteps = 3,
  stage = 'INPUT',
  error = null,
  onRetry = null,
  className = '' 
}) => {
  const steps = [
    {
      id: 1,
      title: 'Prepare',
      description: 'Select and validate Ghana Card image'
    },
    {
      id: 2,
      title: 'Process',
      description: 'Upload and verify document'
    },
    {
      id: 3,
      title: 'Complete',
      description: 'Verification finished'
    }
  ];

  /**
   * Get dynamic step description based on current stage and status
   */
  const getStepDescription = (stepId, currentStage, verificationStatus) => {
    switch (stepId) {
      case 1:
        return currentStage === 'INPUT' ? 'Select Ghana Card image' : 'Image selected ✓';
      case 2:
        if (currentStage === 'UPLOADING') return 'Uploading to secure server...';
        if (currentStage === 'VERIFYING') return 'Verifying with National ID Authority...';
        if (verificationStatus === VERIFIED) return 'Document verified successfully ✓';
        if (verificationStatus === VERIFICATION_FAILED) return 'Verification failed';
        return 'Processing your document';
      case 3:
        if (verificationStatus === VERIFIED) return 'Verification completed successfully ✓';
        if (verificationStatus === VERIFICATION_FAILED) return 'Verification failed - retry needed';
        return 'Awaiting completion';
      default:
        return steps.find(s => s.id === stepId)?.description || '';
    }
  };

  /**
   * Get progress percentage based on current stage
   */
  const getProgressPercentage = (currentStage) => {
    const stageProgress = {
      'INPUT': 10,      // Step 1: 10%
      'UPLOADING': 40,  // Step 2: 40% 
      'VERIFYING': 80,  // Step 2: 80%
      'COMPLETED': 100  // Step 3: 100%
    };
    return stageProgress[currentStage] || 0;
  };

  /**
   * Get current step based on verification stage (simplified logic)
   */
  const getCurrentStep = () => {
    // If currentStep is explicitly provided and valid, use it
    if (currentStep && currentStep > 0 && currentStep <= totalSteps) {
      return currentStep;
    }
    
    // Fallback to status-based calculation only if no currentStep provided
    switch (status) {
      case PENDING_VERIFICATION:
        return 2; // Default to processing step
      case VERIFIED:
        return 3;
      case VERIFICATION_FAILED:
        return 2; // Show as stopped at processing step
      default:
        return 1;
    }
  };

  const activeStep = getCurrentStep();

  /**
   * Get step status styling
   */
  const getStepStatus = (stepId) => {
    if (status === VERIFICATION_FAILED && stepId === 2) {
      return {
        icon: 'error',
        bgColor: 'bg-red-50',
        borderColor: 'border-error',
        textColor: 'text-error',
        iconColor: 'text-error'
      };
    }
    
    if (stepId < activeStep) {
      return {
        icon: 'complete',
        bgColor: 'bg-fresh-leaf',
        borderColor: 'border-fresh-leaf',
        textColor: 'text-fresh-leaf',
        iconColor: 'text-crystal-white'
      };
    }
    
    if (stepId === activeStep) {
      return {
        icon: 'active',
        bgColor: 'bg-crystal-white',
        borderColor: 'border-fresh-leaf',
        textColor: 'text-fresh-leaf',
        iconColor: 'text-fresh-leaf'
      };
    }
    
    return {
      icon: 'pending',
      bgColor: 'bg-silver-mist',
      borderColor: 'border-silver-mist',
      textColor: 'text-steel',
      iconColor: 'text-steel'
    };
  };

  /**
   * Render step icon based on status
   */
  const renderStepIcon = (stepId, stepStatus) => {
    if (stepStatus.icon === 'complete') {
      return (
        <svg className={`h-5 w-5 ${stepStatus.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    
    if (stepStatus.icon === 'error') {
      return (
        <svg className={`h-5 w-5 ${stepStatus.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }
    
    if (stepStatus.icon === 'active') {
      return (
        <svg className={`animate-spin h-5 w-5 ${stepStatus.iconColor}`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }
    
    return (
      <span className={`text-sm font-medium ${stepStatus.iconColor}`}>
        {stepId}
      </span>
    );
  };

  /**
   * Render connector line between steps
   */
  const renderConnector = (stepId) => {
    if (stepId === steps.length) return null;
    
    const isCompleted = stepId < activeStep;
    const isActive = stepId === activeStep - 1;
    
    return (
      <div className="flex-1 flex items-center">
        <div className={`h-1 w-full transition-colors duration-500 ${
          isCompleted ? 'bg-fresh-leaf' : 
          isActive ? 'bg-forest-fern' : 'bg-silver-mist'
        }`} />
      </div>
    );
  };

  return (
    <div className={`verification-progress ${className}`}>
      <div className="bg-crystal-white rounded-lg border border-silver-mist p-6 shadow-soft">
        <h3 className="text-sm font-medium text-steel mb-6 flex items-center gap-2">
          <svg className="h-4 w-4 text-fresh-leaf" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Ghana Card Verification Progress
        </h3>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between space-x-2 md:space-x-4">
          {steps.map((step, index) => {
            const stepStatus = getStepStatus(step.id);
            
            return (
              <React.Fragment key={step.id}>
                {/* Step Circle and Content */}
                <div className="flex flex-col items-center flex-1 min-w-0">
                  {/* Step Circle */}
                  <div className={`
                    flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-500 shadow-soft
                    ${stepStatus.bgColor} ${stepStatus.borderColor}
                  `}>
                    {renderStepIcon(step.id, stepStatus)}
                  </div>
                  
                  {/* Step Text */}
                  <div className="mt-3 text-center">
                    <p className={`text-xs font-semibold ${stepStatus.textColor}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-steel mt-1 max-w-24 leading-relaxed hidden sm:block">
                      {getStepDescription(step.id, stage, status)}
                    </p>
                  </div>
                </div>
                
                {/* Connector Line */}
                {renderConnector(step.id)}
              </React.Fragment>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="bg-silver-mist rounded-full h-3 overflow-hidden border border-silver-mist">
            <div 
              className="bg-gradient-to-r from-fresh-leaf to-forest-fern h-full transition-all duration-700 ease-out relative"
              style={{ width: `${getProgressPercentage(stage)}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse-slow"></div>
            </div>
          </div>
          <p className="text-xs text-center text-steel mt-2 font-medium">
            {getProgressPercentage(stage)}% Complete
          </p>
        </div>

        {/* Current Status Message */}
        <div className="mt-6 text-center">
          {status === PENDING_VERIFICATION && (
            <p className="text-sm text-fresh-leaf flex items-center justify-center gap-2 font-medium">
              {stage === 'INPUT' && '📋 Ready to upload Ghana Card image'}
              {stage === 'UPLOADING' && (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  ⬆️ Uploading Ghana Card to secure server...
                </>
              )}
              {stage === 'VERIFYING' && (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  🔍 Verifying document with National ID Authority...
                </>
              )}
              {stage === 'COMPLETED' && '⏳ Processing verification...'}
            </p>
          )}
          
          {status === VERIFIED && (
            <p className="text-sm text-fresh-leaf flex items-center justify-center gap-2 font-medium">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              ✅ Ghana Card verification completed successfully
            </p>
          )}
          
          {status === VERIFICATION_FAILED && (
            <div className="text-center space-y-4">
              <p className="text-sm text-error flex items-center justify-center gap-2 font-medium">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                ❌ Verification failed - please try uploading again
              </p>
              {error && (
                <div className="text-xs text-error bg-red-50 p-3 rounded border border-red-200 max-w-md mx-auto">
                  <div className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-error mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                </div>
              )}
              {onRetry && (
                <div>
                  <omk-button
                    onClick={onRetry}
                    button-type="filled-button"
                    button-size="medium"
                    className="text-sm"
                  >
                    Try Again
                  </omk-button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationProgress;
